import type { LocalDate } from "@js-joda/core";
import { ScheduleStatus } from "model/Schedule";
import type { Dispatch, RefObject, SetStateAction } from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { isOk } from "utils/result";
import { useCompanyStore } from "web-apps/company/stores/companyStore/companyStore";
import { useScheduleStore } from "web-apps/company/stores/schedule/scheduleStore";
import type { ShiftDetails } from "./components/common/modals/shiftDetailsModal.component";
import type {
  ScheduleCreatePayload,
  ShiftClickPayload,
} from "./components/common/scheduleRow/scheduleRow.component";
import type {
  GroupByOption,
  IntervalOption,
  SizeOption,
} from "./components/hooks/useScheduleToolbarFilters";
import { useScheduleToolbarFilters } from "./components/hooks/useScheduleToolbarFilters";
import type { UnitTeamOption } from "web-apps/company/components/common/filters/unitTeamDropdown.component";
import type { ScheduleWorkerRow } from "./types/schedule.types";
import {
  loadShiftDetails,
  loadShiftStatus,
} from "./utils/schedule.utils.loaders";
import type { PersonOption } from "./utils/schedule.utils.types";

type ShiftBlockSize = "small" | "large" | "pill";
type ShiftDetailsState =
  | { status: "idle" }
  | { status: "loading" }
  | { status: "error" }
  | { status: "ready"; data: ShiftDetails | null };

export interface ScheduleToolbarProps {
  groupByOption: GroupByOption;
  intervalOption: IntervalOption;
  sizeOption: SizeOption;
  unitTeamOptions: UnitTeamOption[];
  selectedWorkGroupIds: string[];
  onSelectedWorkGroupIdsChange: (values: string[]) => void;
  personOptions: PersonOption[];
  unitTeamEmptyText: string;
  personEmptyText: string;
  selectedPersonIds: string[];
  onSelectedPersonIdsChange: (values: string[]) => void;
  onScheduleClick: () => void;
  onGroupByChange: (value: GroupByOption) => void;
  onIntervalChange: (value: IntervalOption) => void;
  onSizeChange: (value: SizeOption) => void;
}

export interface ScheduleViewProps {
  currentDate: LocalDate;
  onChangeDate: (value: LocalDate) => void;
  shiftBlockSize: ShiftBlockSize;
  isLoading: boolean;
  isLoadingMore: boolean;
  isEmpty: boolean;
  emptyText: string;
  groupByOption: GroupByOption;
  workerRows: ScheduleWorkerRow[];
  onShiftClick: (payload: ShiftClickPayload) => void;
  onScheduleClick: (payload: ScheduleCreatePayload) => void;
  loadMoreRef: RefObject<HTMLDivElement>;
}

export interface ScheduleViewModel {
  toolbarProps: ScheduleToolbarProps;
  intervalOption: IntervalOption;
  weeklyViewProps: ScheduleViewProps;
  monthlyViewProps: ScheduleViewProps;
  gigFormPrefill: ScheduleCreatePayload | null;
  quickSchedulePayload: ScheduleCreatePayload | null;
  selectedShift: ShiftClickPayload | null;
  resolvedStatus: ScheduleStatus | undefined;
  translatedWorkerName: string | undefined;
  shiftDetails: ShiftDetails | null;
  isShiftDetailsLoading: boolean;
  shiftDetailsError: boolean;
  isReplaceSuccessOpen: boolean;
  closeReplaceSuccessModal: () => void;
  isGigFormOpen: boolean;
  setGigFormOpen: Dispatch<SetStateAction<boolean>>;
  closeQuickScheduleModal: () => void;
  handleQuickScheduleCreated: () => void;
  handleGigCreated: () => void;
  handleCloseModal: () => void;
  handleShiftEdited: (next: ShiftDetails) => void;
  refreshShiftStatus: () => void;
  handleReplaceSuccess: () => void;
}

/**
 * View-model hook that encapsulates schedule state, side effects, and view props.
 */
export const useScheduleViewModel = (): ScheduleViewModel => {
  const [scheduleState, scheduleActions] = useScheduleStore();
  const [companyState] = useCompanyStore();
  const { t } = useTranslation();
  const scheduleFilters = useScheduleToolbarFilters();
  const companyId = companyState.company?.id;

  const loadMoreRef = useRef<HTMLDivElement | null>(null);
  const wasLoadMoreIntersectingRef = useRef(false);
  const activeShiftRequestIdRef = useRef<number | null>(null);
  const selectedShiftRef = useRef<ShiftClickPayload | null>(null);
  const lastLoadedRowsRef = useRef<ScheduleWorkerRow[]>([]);

  const [selectedShift, setSelectedShift] = useState<ShiftClickPayload | null>(
    null,
  );
  const [shiftDetailsState, setShiftDetailsState] = useState<ShiftDetailsState>(
    { status: "idle" },
  );
  const [isReplaceSuccessOpen, setIsReplaceSuccessOpen] = useState(false);
  const [isGigFormOpen, setIsGigFormOpen] = useState(false);
  const [gigFormPrefill, setGigFormPrefill] =
    useState<ScheduleCreatePayload | null>(null);
  const [quickSchedulePayload, setQuickSchedulePayload] =
    useState<ScheduleCreatePayload | null>(null);

  const workGroupIds = scheduleFilters.workGroupIds;
  const workerIds = scheduleFilters.workerIds;
  const sortBy = scheduleFilters.sortBy;

  const items = useMemo(
    () => scheduleState.schedule?.items ?? [],
    [scheduleState.schedule?.items],
  );
  const isEmpty = Boolean(
    scheduleState.schedule &&
    items.length === 0 &&
    scheduleState.schedule.total === 0,
  );
  const hasMore = Boolean(
    scheduleState.schedule && items.length < scheduleState.schedule.total,
  );

  const intervalLabel = useMemo(
    () =>
      t(
        scheduleFilters.intervalOption === "weekly"
          ? "Schedule.Weekly"
          : "Schedule.Monthly",
      ),
    [scheduleFilters.intervalOption, t],
  );

  const intervalLabelLower = useMemo(() => {
    if (intervalLabel.length === 0) return intervalLabel;
    return intervalLabel[0].toLowerCase() + intervalLabel.slice(1);
  }, [intervalLabel]);

  const shiftBlockSize: ShiftBlockSize =
    scheduleFilters.intervalOption === "monthly"
      ? "pill"
      : scheduleFilters.sizeOption;

  const emptyText = t("Schedule.NoGigs", { interval: intervalLabelLower });

  const resolvedStatus = selectedShift ? selectedShift.item.status : undefined;

  const translatedWorkerName =
    selectedShift?.worker.isWorker === false
      ? t(`Schedule.${selectedShift.worker.heading}`, {
          defaultValue: selectedShift.worker.heading,
        })
      : selectedShift?.worker.heading;

  const shiftDetails =
    shiftDetailsState.status === "ready" ? shiftDetailsState.data : null;
  const isShiftDetailsLoading = shiftDetailsState.status === "loading";
  const shiftDetailsError = shiftDetailsState.status === "error";

  const handleCloseModal = useCallback((): void => {
    setSelectedShift(null);
    activeShiftRequestIdRef.current = null;
    setShiftDetailsState({ status: "idle" });
  }, []);

  const refreshShiftStatus = useCallback((): void => {
    const currentShift = selectedShiftRef.current;
    if (!currentShift || !companyId) return;

    loadShiftStatus(
      companyId,
      currentShift.item.gigShiftId,
      currentShift.worker.id,
    ).then((result) => {
      if (!isOk(result)) return;
      const nextStatus = result.value.status;
      const nextDetails = result.value.details;
      setSelectedShift((prev) => {
        if (!prev || prev.item.gigShiftId !== currentShift.item.gigShiftId) {
          return prev;
        }
        return {
          ...prev,
          item: {
            ...prev.item,
            status: nextStatus,
            details: nextDetails,
          },
        };
      });

      scheduleActions.updateShift(currentShift.item.gigShiftId, {
        status: nextStatus,
        details: nextDetails,
      });
    });
  }, [companyId, scheduleActions]);

  const handleShiftEdited = useCallback(
    (next: ShiftDetails): void => {
      setShiftDetailsState({ status: "ready", data: next });
      const currentShift = selectedShiftRef.current;
      if (!currentShift) return;

      setSelectedShift({
        ...currentShift,
        item: {
          ...currentShift.item,
          startTime: next.startTime,
          endTime: next.endTime,
        },
      });

      scheduleActions.updateShift(currentShift.item.gigShiftId, {
        startTime: next.startTime,
        endTime: next.endTime,
      });
    },
    [scheduleActions],
  );

  const handleShiftClick = useCallback(
    (payload: ShiftClickPayload): void => {
      const requestId = payload.item.gigShiftId;
      activeShiftRequestIdRef.current = requestId;
      setSelectedShift(payload);

      if (
        payload.modalTarget === "teamPublicDetails" ||
        payload.item.status === ScheduleStatus.NOT_AVAILABLE
      ) {
        setShiftDetailsState({ status: "idle" });
        return;
      }

      setShiftDetailsState({ status: "loading" });

      if (!companyId) {
        setShiftDetailsState({ status: "error" });
        return;
      }
      loadShiftDetails(
        companyId,
        payload.item.gigId,
        payload.item.gigShiftId,
        payload.item.gigType,
      )
        .then((result) => {
          if (activeShiftRequestIdRef.current !== requestId) return;
          if (isOk(result)) {
            setShiftDetailsState({ status: "ready", data: result.value });
            return;
          }
          setShiftDetailsState({ status: "error" });
        })
        .catch(() => {
          if (activeShiftRequestIdRef.current !== requestId) return;
          setShiftDetailsState({ status: "error" });
        });
    },
    [companyId],
  );

  const handleLoadMore = useCallback((): void => {
    if (!companyId || !hasMore || scheduleState.isLoading) return;
    if (scheduleState.isLoadingMore) return;
    scheduleActions.getSchedule(
      companyId,
      scheduleFilters.range.fromDate,
      scheduleFilters.range.toDate,
      scheduleState.page + 1,
      scheduleFilters.pageSize,
      sortBy,
      workGroupIds,
      workerIds,
    );
  }, [
    companyId,
    hasMore,
    scheduleActions,
    scheduleFilters.pageSize,
    scheduleFilters.range.fromDate,
    scheduleFilters.range.toDate,
    scheduleState.isLoading,
    scheduleState.isLoadingMore,
    scheduleState.page,
    sortBy,
    workGroupIds,
    workerIds,
  ]);

  const refreshSchedule = useCallback((): void => {
    if (!companyId) return;
    scheduleActions.resetSchedule();
    wasLoadMoreIntersectingRef.current = false;

    scheduleActions.getSchedule(
      companyId,
      scheduleFilters.range.fromDate,
      scheduleFilters.range.toDate,
      0,
      scheduleFilters.pageSize,
      sortBy,
      workGroupIds,
      workerIds,
    );
  }, [
    companyId,
    scheduleActions,
    scheduleFilters.pageSize,
    scheduleFilters.range.fromDate,
    scheduleFilters.range.toDate,
    sortBy,
    workGroupIds,
    workerIds,
  ]);

  const handleReplaceSuccess = useCallback((): void => {
    handleCloseModal();
    refreshSchedule();
    setIsReplaceSuccessOpen(true);
  }, [handleCloseModal, refreshSchedule]);

  const closeQuickScheduleModal = useCallback((): void => {
    setQuickSchedulePayload(null);
  }, []);

  const handleQuickScheduleCreated = useCallback((): void => {
    setQuickSchedulePayload(null);
    refreshSchedule();
  }, [refreshSchedule]);

  const handleGigCreated = useCallback((): void => {
    refreshSchedule();
  }, [refreshSchedule]);

  useEffect(() => {
    selectedShiftRef.current = selectedShift;
  }, [selectedShift]);

  useEffect(() => {
    if (items.length === 0) return;
    lastLoadedRowsRef.current = items;
  }, [items]);

  useEffect(() => {
    if (!scheduleFilters.isPreferencesReady) return;
    if (!companyId) return;
    scheduleActions.resetSchedule();
    scheduleActions.getSchedule(
      companyId,
      scheduleFilters.range.fromDate,
      scheduleFilters.range.toDate,
      0,
      scheduleFilters.pageSize,
      sortBy,
      workGroupIds,
      workerIds,
    );
  }, [
    companyId,
    scheduleActions,
    scheduleFilters.isPreferencesReady,
    scheduleFilters.pageSize,
    scheduleFilters.range.fromDate,
    scheduleFilters.range.toDate,
    sortBy,
    workGroupIds,
    workerIds,
  ]);

  useEffect(() => {
    if (!loadMoreRef.current || !hasMore || scheduleState.isLoadingMore) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const isIntersecting = Boolean(entries[0]?.isIntersecting);
        if (isIntersecting && !wasLoadMoreIntersectingRef.current) {
          handleLoadMore();
        }
        wasLoadMoreIntersectingRef.current = isIntersecting;
      },
      { root: null, rootMargin: "200px", threshold: 0 },
    );
    observer.observe(loadMoreRef.current);
    return () => observer.disconnect();
  }, [handleLoadMore, hasMore, scheduleState.isLoadingMore]);

  const toolbarProps: ScheduleToolbarProps = {
    groupByOption: scheduleFilters.groupByOption,
    intervalOption: scheduleFilters.intervalOption,
    sizeOption: scheduleFilters.sizeOption,
    unitTeamOptions: scheduleFilters.unitTeamOptions,
    selectedWorkGroupIds: scheduleFilters.selectedWorkGroupIds,
    onSelectedWorkGroupIdsChange: scheduleFilters.setSelectedWorkGroupIds,
    personOptions: scheduleFilters.personOptions,
    unitTeamEmptyText: scheduleFilters.unitTeamLoadError
      ? t("OrganizationalStructure.UnitTeamLoadFailed")
      : t("OrganizationalStructure.NoUnitTeamMatches"),
    personEmptyText: scheduleFilters.workersLoadError
      ? t("OrganizationalStructure.WorkersLoadFailed")
      : t("OrganizationalStructure.NoPersonMatches"),
    selectedPersonIds: scheduleFilters.selectedPersonIds,
    onSelectedPersonIdsChange: scheduleFilters.setSelectedPersonIds,
    onScheduleClick: () => {
      setGigFormPrefill(null);
      setIsGigFormOpen(true);
    },
    onGroupByChange: scheduleFilters.setGroupByOption,
    onIntervalChange: scheduleFilters.setIntervalOption,
    onSizeChange: scheduleFilters.setSizeOption,
  };

  const viewPropsBase = {
    shiftBlockSize,
    isLoading: scheduleState.isLoading,
    isLoadingMore: scheduleState.isLoadingMore,
    isEmpty:
      scheduleState.isLoading &&
      scheduleState.schedule === null &&
      lastLoadedRowsRef.current.length > 0
        ? false
        : isEmpty,
    emptyText,
    groupByOption: scheduleFilters.groupByOption,
    workerRows:
      scheduleState.isLoading &&
      scheduleState.schedule === null &&
      lastLoadedRowsRef.current.length > 0
        ? lastLoadedRowsRef.current
        : items,
    onShiftClick: handleShiftClick,
    onScheduleClick: (payload: ScheduleCreatePayload) => {
      setQuickSchedulePayload(payload);
    },
    loadMoreRef,
  };

  const weeklyViewProps: ScheduleViewProps = {
    ...viewPropsBase,
    currentDate: scheduleFilters.weekDate,
    onChangeDate: scheduleFilters.setWeekDate,
  };

  const monthlyViewProps: ScheduleViewProps = {
    ...viewPropsBase,
    currentDate: scheduleFilters.monthDate,
    onChangeDate: scheduleFilters.setMonthDate,
  };

  return {
    toolbarProps,
    intervalOption: scheduleFilters.intervalOption,
    weeklyViewProps,
    monthlyViewProps,
    gigFormPrefill,
    quickSchedulePayload,
    selectedShift,
    resolvedStatus,
    translatedWorkerName,
    shiftDetails,
    isShiftDetailsLoading,
    shiftDetailsError,
    isReplaceSuccessOpen,
    closeReplaceSuccessModal: () => setIsReplaceSuccessOpen(false),
    isGigFormOpen,
    setGigFormOpen: (next) => {
      setIsGigFormOpen((prev) => {
        const resolved = typeof next === "function" ? next(prev) : next;
        if (!resolved) setGigFormPrefill(null);
        return resolved;
      });
    },
    closeQuickScheduleModal,
    handleQuickScheduleCreated,
    handleGigCreated,
    handleCloseModal,
    handleShiftEdited,
    refreshShiftStatus,
    handleReplaceSuccess,
  };
};
